import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import {
  RackReservation,
  EquipmentSlotItem,
  SpaceMetrics,
  buildReservationMap,
  validateReservationPlacement,
  removeReservation as removeResLogic,
} from '../utils/rackReservation.logic';
import { calculateRackMetrics } from '../utils/rackMetrics.logic';
import toast from 'react-hot-toast';

export function useRackReservations(
  rackId: string | number | undefined,
  totalU: number,
  equipmentList: EquipmentSlotItem[]
) {
  const queryClient = useQueryClient();
  const queryKey = ['rackReservations', String(rackId)];
  const { data: reservations = [], isLoading, isError } = useQuery<RackReservation[]>({
    queryKey,
    enabled: !!rackId,
    retry: false,
    queryFn: async () => {
      const response = await api.get(`/api/racks/${rackId}/reservations`);
      if (!Array.isArray(response.data)) throw new Error('Некорректный ответ резервирования');
      return response.data.map(item => ({ ...item, id: String(item.id) }));
    },
  });

  const reservationMap = useMemo(() => buildReservationMap(reservations), [reservations]);

  const spaceMetrics: SpaceMetrics = useMemo(() => {
    return calculateRackMetrics(equipmentList, reservations, totalU);
  }, [totalU, equipmentList, reservations]);

  const add = useCallback(async (newRes: Omit<RackReservation, 'id'>) => {
    if (!rackId || isLoading || isError) return { success: false, error: 'Сведения о резервах недоступны.' };
    const result = validateReservationPlacement(reservations, equipmentList, newRes.u_start, newRes.u_height, totalU, undefined, newRes.face);
    if (!result.valid) return { success: false, error: result.conflictReason };
    try {
      const response = await api.post(`/api/racks/${rackId}/reservations`, newRes, { timeout: 30000 });
      if (!response.data?.id) throw new Error('Подтверждение резервирования не получено.');
      queryClient.setQueryData<RackReservation[]>(['rackReservations', String(rackId)], prev =>
        [...(prev ?? []), { ...response.data, id: String(response.data.id) }]);
      return { success: true };
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      await queryClient.invalidateQueries({ queryKey: ['rackReservations', String(rackId)] });
      return { success: false, error: typeof detail === 'string' ? detail : 'Подтверждение резервирования не получено. Проверьте резервы перед повтором.' };
    }
  }, [reservations, equipmentList, totalU, rackId, isLoading, isError, queryClient]);

  const remove = useCallback(async (id: string) => {
    try {
    await api.delete(`/api/racks/${rackId}/reservations/${id}`);
    queryClient.setQueryData<RackReservation[]>(['rackReservations', String(rackId)], prev => removeResLogic(prev ?? [], id));
    } catch {
      await queryClient.invalidateQueries({ queryKey: ['rackReservations', String(rackId)] });
      toast.error('Снятие резерва не подтверждено. Проверьте данные перед повтором.');
    }
  }, [rackId, queryClient]);

  return {
    reservations,
    isLoading,
    isError,
    reservationMap,
    spaceMetrics,
    addReservation: add,
    removeReservation: remove,
  };
}
